<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { SbList, SbListItem, SbListLabel, SbListSection } from "@unio/saltbox-ui"
import { computed, shallowRef } from "vue"

import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue"
import FlashAlerts from "@/components/FlashAlerts.vue"
import NarrowPage from "@/components/NarrowPage.vue"
import { useNavbar } from "@/composables/useNavbar"
import { formatDate } from "@/lib/format"
import { ICON } from "@/lib/icons"

interface AccessTokenEntry {
  id: string
  description: string
  permission: "read" | "write"
  token_prefix: string
  active: boolean
  last_used_at: string | null
  expires_at: string | null
  revoked_at: string | null
  created_at: string
}

const props = defineProps<{
  tokens: AccessTokenEntry[]
}>()

const { title } = useNavbar({ title: "API Tokens" })

const revokeTarget = shallowRef<AccessTokenEntry | null>(null)
const revoking = shallowRef(false)
const revokeOpen = computed(() => revokeTarget.value !== null)

function accessTokenPath(id: string): string {
  return `/account/access_tokens/${encodeURIComponent(id)}`
}

function confirmRevoke() {
  if (!revokeTarget.value) return

  revoking.value = true
  router.delete(accessTokenPath(revokeTarget.value.id), {
    preserveScroll: true,
    onFinish: () => {
      revoking.value = false
      revokeTarget.value = null
    },
  })
}

function openRevoke(token: AccessTokenEntry) {
  revokeTarget.value = token
}

function closeRevoke(open: boolean) {
  if (!open) revokeTarget.value = null
}

function statusLabel(token: AccessTokenEntry): string {
  if (token.revoked_at) return "Revoked"
  if (token.expires_at && new Date(token.expires_at) <= new Date()) return "Expired"
  return "Active"
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <FlashAlerts />

    <div class="flex items-center justify-between gap-4">
      <p class="text-muted text-sm">Use tokens to connect agents and command-line tools.</p>
      <UButton to="/account/access_tokens/new" :icon="ICON.add" label="New token" size="sm" />
    </div>

    <SbList v-if="props.tokens.length" bordered separator>
      <SbListItem v-for="token in props.tokens" :key="token.id">
        <SbListSection>
          <SbListLabel :lines="1">{{ token.description }}</SbListLabel>
          <SbListLabel caption>
            <span class="font-mono text-xs">{{ token.token_prefix }}…</span>
            · {{ token.permission }} · Created {{ formatDate(token.created_at) }}
          </SbListLabel>
          <SbListLabel v-if="token.last_used_at" caption>
            Last used {{ formatDate(token.last_used_at) }}
          </SbListLabel>
        </SbListSection>
        <SbListSection side>
          <div class="flex items-center gap-2">
            <UBadge
              :color="token.active ? 'success' : 'neutral'"
              :label="statusLabel(token)"
              variant="subtle"
              size="sm"
            />
            <UButton
              v-if="token.active"
              :icon="ICON.delete"
              label="Revoke"
              color="error"
              variant="ghost"
              size="xs"
              @click="openRevoke(token)"
            />
          </div>
        </SbListSection>
      </SbListItem>
    </SbList>

    <UEmpty
      v-else
      :icon="ICON.apiToken"
      title="No API tokens"
      description="Create a token when you're ready to connect the Ninjanizr CLI or an agent."
      :actions="[{ label: 'Create token', to: '/account/access_tokens/new' }]"
    />

    <DeleteConfirmModal
      :open="revokeOpen"
      title="Revoke this API token?"
      :loading="revoking"
      action-label="Revoke"
      @update:open="closeRevoke"
      @confirm="confirmRevoke"
    >
      <p class="text-sm">
        {{ revokeTarget?.description }} will immediately stop working. This cannot be undone.
      </p>
    </DeleteConfirmModal>
  </NarrowPage>
</template>
